import { OrderSourceModel } from '@/http/models/OrderSource'
import { toast } from 'sonner'

import { useState } from 'react'

import { OrderSourceModel as OrderSourceType } from '@/types'

import { useFetchOrderSources } from './useFetchOrderSources'

type DialogMode = 'create' | 'edit' | 'view'

export const useOrderSourcesTable = () => {
  const [mode, setMode] = useState<DialogMode>('create')
  const [orderSourceDialogOpen, setOrderSourceDialogOpen] = useState(false)
  const [editOrderSource, setEditOrderSource] = useState<OrderSourceType.OrderSource | null>(null)

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [orderSourceToDelete, setOrderSourceToDelete] =
    useState<OrderSourceType.OrderSource | null>(null)

  const [createLoading, setCreateLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchOrderSources({
      sort: null,
    })

  const openCreateDialog = () => {
    setMode('create')
    setEditOrderSource(null)
    setOrderSourceDialogOpen(true)
  }

  const openViewDialog = (orderSource: OrderSourceType.OrderSource) => {
    setMode('view')
    setEditOrderSource(orderSource)
    setOrderSourceDialogOpen(true)
  }

  const openEditDialog = (orderSource: OrderSourceType.OrderSource) => {
    setMode('edit')
    setEditOrderSource(orderSource)
    setOrderSourceDialogOpen(true)
  }

  const closeDialog = () => {
    setOrderSourceDialogOpen(false)
    setEditOrderSource(null)
  }

  const handleDeleteClick = (orderSource: OrderSourceType.OrderSource) => {
    setOrderSourceToDelete(orderSource)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!orderSourceToDelete?._id) return

    setDeleteLoading(true)
    const response = await OrderSourceModel.delete({
      params: { id: orderSourceToDelete._id },
    })

    if (response.success) {
      toast.success('Order source deleted successfully')
      setDeleteDialogOpen(false)
      setOrderSourceToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete order source')
    }
    setDeleteLoading(false)
  }

  const toggleStatus = async (orderSource: OrderSourceType.OrderSource) => {
    const newStatus = orderSource.status === 'active' ? 'inactive' : 'active'

    const response = await OrderSourceModel.update({
      params: { id: orderSource._id },
      body: { status: newStatus, name: orderSource.name },
    })

    if (response.success) {
      toast.success('Order source status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  const handleCreateOrderSource = async (data: any) => {
    setCreateLoading(true)
    const response = await OrderSourceModel.create({
      body: data,
    })

    if (response.success) {
      toast.success('Order source created successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to create order source')
    }
    setCreateLoading(false)
  }

  const handleUpdateOrderSource = async (id: string, data: any) => {
    setUpdateLoading(true)
    const response = await OrderSourceModel.update({
      params: { id },
      body: data,
    })

    if (response.success) {
      toast.success('Order source updated successfully')
      closeDialog()
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to update order source')
    }
    setUpdateLoading(false)
  }

  const stats = {
    totalOrderSources: pagination.totalCount,
    activeOrderSources: data.filter((source) => source.status === 'active').length,
    inactiveOrderSources: data.filter((source) => source.status === 'inactive').length,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,
    mode,
    orderSourceDialogOpen,
    editOrderSource,
    deleteDialogOpen,
    orderSourceToDelete,
    createLoading,
    updateLoading,
    deleteLoading,
    setPage,
    setPageSize,
    refresh,
    openCreateDialog,
    openViewDialog,
    openEditDialog,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    handleCreateOrderSource,
    handleUpdateOrderSource,
    toggleStatus,
  }
}
